import React, { useCallback, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import { useNavigate } from 'react-router-dom';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationCounts,
  useNotifications,
} from '../../hooks/queries/notificationQueries';
import MockInterviewNotificationPrefs from './MockInterviewNotificationPrefs';
import { isMockInterviewsEnabled } from '../../config/placementInsightsFlags';

function formatRelativeTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function NotificationRow({ notification, onRead, onNavigate }) {
  const handleClick = () => {
    if (!notification.isRead) onRead(notification._id);
    const link = notification.metadata?.link;
    if (link) onNavigate(link);
  };

  return (
    <ListItem disablePadding sx={{ bgcolor: notification.isRead ? 'transparent' : 'action.hover' }}>
      <ListItemButton onClick={handleClick} alignItems="flex-start" sx={{ py: 1.25 }}>
        <ListItemText
          primary={(
            <Typography variant="body2" fontWeight={notification.isRead ? 500 : 700} noWrap>
              {notification.title}
            </Typography>
          )}
          secondary={(
            <Box component="span" sx={{ display: 'block' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {notification.message}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                {formatRelativeTime(notification.createdAt)}
              </Typography>
            </Box>
          )}
        />
      </ListItemButton>
    </ListItem>
  );
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [limit, setLimit] = useState(20);

  const { data: unreadCount = 0 } = useNotificationCounts({ enabled: true });
  const { data, isLoading, isFetching } = useNotifications(
    { page: 1, limit },
    { enabled: open },
  );
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const notifications = data?.notifications ?? [];
  const pagination = data?.pagination ?? {};
  const readCount = data?.readCount ?? 0;
  const totalUnread = data?.unreadCount ?? unreadCount;

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleRead = useCallback((id) => {
    markRead.mutate(id);
  }, [markRead]);

  const handleMarkAllRead = useCallback(() => {
    markAllRead.mutate();
  }, [markAllRead]);

  const handleNavigate = useCallback((link) => {
    setOpen(false);
    navigate(link);
  }, [navigate]);

  const canLoadMore = useMemo(
    () => notifications.length < (pagination.total ?? 0),
    [notifications.length, pagination.total],
  );

  return (
    <>
      <IconButton
        color="inherit"
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        onClick={handleOpen}
        size="small"
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          p: 0.75,
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={99}
          invisible={!unreadCount}
        >
          <NotificationsNoneRoundedIcon fontSize="small" />
        </Badge>
      </IconButton>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 400 }, maxWidth: '100vw' },
        }}
      >
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>
              Notifications
            </Typography>
            {totalUnread > 0 ? (
              <Button
                size="small"
                startIcon={<DoneAllRoundedIcon />}
                onClick={handleMarkAllRead}
                disabled={markAllRead.isPending}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Mark all read
              </Button>
            ) : null}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {totalUnread} unread · {readCount} read
          </Typography>
        </Box>
        <Divider />

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 6, textAlign: 'center' }}>
            <NotificationsNoneRoundedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications yet.
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
              Assignment and mock interview updates will appear here.
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ flex: 1, overflow: 'auto' }}>
            {notifications.map((notification) => (
              <NotificationRow
                key={notification._id}
                notification={notification}
                onRead={handleRead}
                onNavigate={handleNavigate}
              />
            ))}
          </List>
        )}

        {canLoadMore ? (
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              onClick={() => setLimit((value) => value + 20)}
              disabled={isFetching}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Load more
            </Button>
          </Box>
        ) : null}

        {isMockInterviewsEnabled() ? (
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <MockInterviewNotificationPrefs />
          </Box>
        ) : null}
      </Drawer>
    </>
  );
}
